import { supabase } from './supabase'

/**
 * #/admin/stars/endorsements (was #/stars/admin → Endorsements until 9 Oct). 128, David 30 Sep.
 *
 * Curation AFTER the fact, never a gate in front: anybody can endorse for
 * anything, and this is where a Star One admin tidies what people typed —
 * makes a good word official (and translates it), features it as a chip on
 * the give form, hides it from the suggestions, or merges a duplicate into it.
 *
 * All four writes are the server's to refuse (stars.admin, unscoped — the same
 * permission as the earning rules), and every read shows what the table holds
 * after the write rather than a splice (028c).
 *
 * ── COUNTS ARE PER WORD, NEVER PER PERSON ───────────────────────────────────
 *
 * `uses` is how many live recognitions carry the word. There is no "who used
 * it" and no "who is endorsed most" anywhere — D10 / B22. A per-person count of
 * endorsements is the leaderboard through another door.
 */
export type EndorsementAlias = { id: string; label: string; uses: number }

export type EndorsementAdminRow = {
  id: string
  slug: string
  /** The first spelling anybody used — what a community word shows. */
  label: string
  labelEn: string | null
  labelEs: string | null
  isOfficial: boolean
  isFeatured: boolean
  isHidden: boolean
  sortOrder: number
  /** The pre-128 recognition category this row replaced, if any. */
  categoryKey: string | null
  /** Live recognitions carrying it or any alias merged into it. */
  uses: number
  aliases: EndorsementAlias[]
  createdAt: string
}

/** The whole vocabulary. NO LIMIT, by design — the screen may filter it (rule 1's exception). */
export async function loadEndorsementAdmin(): Promise<EndorsementAdminRow[]> {
  const { data, error } = await supabase.rpc('endorsement_admin_list')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.endorsement_id as string,
    slug: r.slug as string,
    label: r.label as string,
    labelEn: (r.label_en as string) ?? null,
    labelEs: (r.label_es as string) ?? null,
    isOfficial: Boolean(r.is_official),
    isFeatured: Boolean(r.is_featured),
    isHidden: Boolean(r.is_hidden),
    sortOrder: (r.sort_order as number) ?? 0,
    categoryKey: (r.category_key as string) ?? null,
    uses: (r.uses as number) ?? 0,
    aliases: Array.isArray(r.aliases)
      ? (r.aliases as Record<string, unknown>[]).map((a) => ({
          id: a.id as string,
          label: a.label as string,
          uses: (a.uses as number) ?? 0,
        }))
      : [],
    createdAt: r.created_at as string,
  }))
}

/** What a row shows: the English label for an official word, the typed one otherwise. */
export function adminLabel(r: Pick<EndorsementAdminRow, 'isOfficial' | 'labelEn' | 'label'>): string {
  return (r.isOfficial && r.labelEn) || r.label
}

export type EndorsementSave = {
  id: string
  isOfficial: boolean
  isFeatured: boolean
  isHidden: boolean
  sortOrder: number
  /** Required when official; must be null when not. */
  labelEn: string | null
  /** Optional when official (null removes it); must be null when not. */
  labelEs: string | null
}

/** A REPLACE (rule 9): every field, every time. */
export async function saveEndorsement(input: EndorsementSave): Promise<void> {
  const { error } = await supabase.rpc('save_endorsement', {
    p_endorsement_id: input.id,
    p_is_official: input.isOfficial,
    p_is_featured: input.isOfficial && input.isFeatured,
    p_is_hidden: input.isHidden,
    p_sort_order: input.sortOrder,
    p_label_en: input.isOfficial ? input.labelEn : null,
    p_label_es: input.isOfficial ? input.labelEs : null,
  })
  if (error) throw error
}

export async function mergeEndorsement(fromId: string, intoId: string): Promise<void> {
  const { error } = await supabase.rpc('merge_endorsement', { p_from: fromId, p_into: intoId })
  if (error) throw error
}

export async function unmergeEndorsement(id: string): Promise<void> {
  const { error } = await supabase.rpc('unmerge_endorsement', { p_id: id })
  if (error) throw error
}

/**
 * The screen's search. Client-side ON PURPOSE: the read has no limit, so the
 * browser holds every row (standing rule 1's stated exception).
 */
export function matchesAdminQuery(r: EndorsementAdminRow, query: string): boolean {
  const q = query.trim().toLocaleLowerCase()
  if (!q) return true
  return [r.label, r.labelEn, r.labelEs, r.slug, ...r.aliases.map((a) => a.label)]
    .some((v) => (v ?? '').toLocaleLowerCase().includes(q))
}

/**
 * The page's pill filters (9 Oct). OR within a group, AND across groups; an
 * empty group is no filter. A FILTER, never an order — the list stays in the
 * server's order (nothing here ranks a word by its uses).
 */
export type EndorsementFlag = 'featured' | 'hidden' | 'merged'
export type EndorsementFilters = {
  query: string
  kinds: ('official' | 'community')[]
  flags: EndorsementFlag[]
  spanish: ('has' | 'missing')[]
}

export function matchesEndorsementFilters(r: EndorsementAdminRow, f: EndorsementFilters): boolean {
  if (!matchesAdminQuery(r, f.query)) return false
  if (f.kinds.length && !f.kinds.includes(r.isOfficial ? 'official' : 'community')) return false
  if (f.flags.length) {
    const has: Record<EndorsementFlag, boolean> = {
      featured: r.isFeatured,
      hidden: r.isHidden,
      merged: r.aliases.length > 0,
    }
    if (!f.flags.some((k) => has[k])) return false
  }
  // Spanish is a question about OFFICIAL words — a community word shows as it
  // was typed and has no translation to be missing.
  if (f.spanish.length) {
    if (!r.isOfficial) return false
    if (!f.spanish.includes(r.labelEs ? 'has' : 'missing')) return false
  }
  return true
}
